import { useState, useMemo, type FC } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, AlertCircle } from 'lucide-react';
import { BookingCard } from '../../../components/Dashboard';
import { useMyBookings, useCancelBooking } from '../../../hooks/useBookings';
import { useToast } from '../../../components/ui/Toast';
import { ConfirmDeleteDialog } from './ConfirmDeleteDialog';
import type { BookingResponse } from '../../../types/booking.types';

export const UpcomingBookingsSection: FC = () => {
  const { data: bookings = [], isLoading, error, refetch } = useMyBookings();
  const cancelMutation = useCancelBooking();
  const { showToast } = useToast();
  const [cancelTarget, setCancelTarget] = useState<BookingResponse | null>(null);

  const upcomingBookings = useMemo(() => {
    const now = new Date();
    return bookings.filter((b) => new Date(b.startTime) > now && b.status !== 'CANCELLED');
  }, [bookings]);

  const handleCancelRequest = (id: string) => {
    const booking = bookings.find((b) => b.id === id);
    if (booking) {
      setCancelTarget(booking);
    }
  };

  const handleConfirmCancel = async () => {
    if (!cancelTarget) return;

    try {
      await cancelMutation.mutateAsync(cancelTarget.id);
      showToast({
        type: 'success',
        title: 'Booking Cancelled',
        message: 'Your appointment has been cancelled successfully.',
      });
      setCancelTarget(null);
    } catch {
      // Error handled by mutation - dialog stays open to show error state
    }
  };

  const handleCloseDialog = () => {
    if (!cancelMutation.isPending) {
      setCancelTarget(null);
    }
  };

  const formatBookingDateTime = (startTime: string): string => {
    const date = new Date(startTime);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Upcoming Bookings</h1>
        <p className="text-gray-600 dark:text-slate-400 mt-1">
          You have {upcomingBookings.length} upcoming{' '}
          {upcomingBookings.length === 1 ? 'appointment' : 'appointments'}
        </p>
      </div>

      {(error || cancelMutation.error) && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center">
          <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mr-2 flex-shrink-0" />
          <p className="text-red-800 dark:text-red-300">{error?.message || cancelMutation.error?.message}</p>
          <button
            onClick={() => refetch()}
            className="ml-auto text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 font-medium"
          >
            Retry
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-4 animate-pulse">
              <div className="flex justify-between mb-3">
                <div>
                  <div className="h-5 w-40 bg-gray-200 dark:bg-slate-700 rounded mb-2" />
                  <div className="h-4 w-24 bg-gray-200 dark:bg-slate-700 rounded" />
                </div>
                <div className="h-6 w-20 bg-gray-200 dark:bg-slate-700 rounded-full" />
              </div>
              <div className="grid grid-cols-2 gap-2 mb-4">
                <div className="h-4 w-28 bg-gray-200 dark:bg-slate-700 rounded" />
                <div className="h-4 w-24 bg-gray-200 dark:bg-slate-700 rounded" />
                <div className="h-4 w-20 bg-gray-200 dark:bg-slate-700 rounded" />
                <div className="h-4 w-16 bg-gray-200 dark:bg-slate-700 rounded" />
              </div>
              <div className="flex gap-2">
                <div className="h-10 flex-1 bg-gray-200 dark:bg-slate-700 rounded" />
                <div className="h-10 w-20 bg-gray-200 dark:bg-slate-700 rounded" />
              </div>
            </div>
          ))}
        </div>
      ) : upcomingBookings.length > 0 ? (
        <div className="space-y-4">
          {upcomingBookings.map((booking) => (
            <BookingCard
              key={booking.id}
              booking={booking}
              onCancel={handleCancelRequest}
              isCancelling={cancelTarget?.id === booking.id && cancelMutation.isPending}
              showActions
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700">
          <Calendar className="w-16 h-16 text-gray-400 dark:text-slate-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-2">No Upcoming Bookings</h3>
          <p className="text-gray-600 dark:text-slate-400 mb-6">Ready to book your next massage therapy session?</p>
          <Link
            to="/services"
            className="inline-flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
          >
            Browse Services
          </Link>
        </div>
      )}

      {/* Cancel Confirmation Dialog */}
      <ConfirmDeleteDialog
        isOpen={cancelTarget !== null}
        onClose={handleCloseDialog}
        onConfirm={handleConfirmCancel}
        isLoading={cancelMutation.isPending}
        title="Cancel Booking"
        message={
          cancelTarget
            ? `Are you sure you want to cancel your ${cancelTarget.service?.name || 'appointment'} on ${formatBookingDateTime(cancelTarget.startTime)}? This action cannot be undone.`
            : ''
        }
        confirmText="Cancel Booking"
        cancelText="Keep Booking"
      />
    </div>
  );
};